import { Compass, ClipboardCheck, BarChart3, Handshake } from "lucide-react";
import { HOW } from "@/constants/testIds";
import { useVerificationDialog } from "@/contexts/VerificationDialogContext";

const items = [
  {
    icon: Compass,
    step: "Step 1",
    title: "Sourcing Brief",
    description:
      "We open with a confidential consultation to capture your sourcing brief — product specifications, target volumes, quality standards, lead-time expectations, and budget envelope.",
    bullets: [
      "Confidential intake call",
      "Product & specification mapping",
      "Volume, lead-time & budget scope",
    ],
    testId: HOW.cardVerification,
  },
  {
    icon: ClipboardCheck,
    step: "Step 2",
    title: "Supplier Matchmaking",
    description:
      "Our on-the-ground team in the Dominican Republic shortlists qualified suppliers, runs capability and reliability checks, and presents you with a curated match list — never a generic directory.",
    bullets: [
      "Dominican Republic supplier shortlist",
      "Capability & reliability checks",
      "Curated, evidence-backed match list",
    ],
    testId: HOW.cardConsultation,
  },
  {
    icon: BarChart3,
    step: "Step 3",
    title: "Negotiation & Liaison",
    description:
      "We facilitate negotiations on pricing, MOQs, lead times and quality terms, then act as your ongoing trade liaison so the US–DR relationship runs without setbacks after the deal is signed.",
    bullets: [
      "Cross-border commercial negotiation",
      "Contract & terms facilitation",
      "Ongoing US–DR trade liaison",
    ],
    testId: HOW.cardDelivery,
  },
];

export default function HowItWorks() {
  const { openDialog } = useVerificationDialog();

  return (
    <section
      id="methodology"
      data-testid={HOW.section}
      className="bg-slate-50/60 pt-12 lg:pt-16 pb-20 lg:pb-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="eyebrow">How It Works</div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900">
            From sourcing brief to signed supplier —{" "}
            <span className="font-semibold">one disciplined pathway.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
            Every PS GLOBAL SERIES engagement follows the same three-step
            sourcing workflow — designed for procurement, operations, and
            commercial leaders who need a defensible US–DR trade pipeline.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <article
                key={it.title}
                data-testid={it.testId}
                className="group relative bg-white border border-slate-200 p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.18)] hover:border-slate-300"
              >
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 inline-flex items-center justify-center bg-slate-900 text-white rounded-sm">
                    <Icon size={20} />
                  </div>
                  <div className="eyebrow text-[#B45309]">{it.step}</div>
                </div>

                <h3 className="font-display mt-7 text-2xl font-semibold tracking-tight text-slate-900">
                  {it.title}
                </h3>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
                  {it.description}
                </p>

                <ul className="mt-6 space-y-2">
                  {it.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 text-sm text-slate-700"
                    >
                      <span className="mt-2 h-px w-3 bg-[#B45309]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#B45309]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </article>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8 border-t border-slate-200 pt-10">
          <div className="text-slate-700 text-[15px] max-w-xl">
            Have a specific category in mind?{" "}
            <span className="font-semibold text-slate-900">
              Begin with a sourcing brief
            </span>{" "}
            — share what you need and our trade-liaison team will reach out.
          </div>
          <button
            type="button"
            onClick={openDialog}
            data-testid="how-cta-whatsapp"
            className="inline-flex items-center justify-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3.5 text-sm font-semibold tracking-tight rounded-sm transition-all shadow-sm hover:shadow-md"
          >
            <Handshake size={16} />
            Tell Us What You Need Sourced
          </button>
        </div>
      </div>
    </section>
  );
}
